import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtErr } from "@/lib/api";
import { Lock, User, Sparkles } from "lucide-react";
import { toast } from "sonner";
import BrandMark from "@/components/BrandMark";

export default function AdminLogin() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { username, password });
      if (data.role === "customer") {
        localStorage.removeItem("sms_admin_token");
        localStorage.setItem("sms_portal_token", data.portal_token);
        localStorage.setItem("sms_portal_user", data.username || "");
        localStorage.setItem("sms_portal_customer", data.customer_name || "");
        toast.success(`Hoş geldiniz · ${data.customer_name || data.username}`);
        nav("/portal");
      } else {
        localStorage.setItem("sms_admin_token", data.access_token);
        localStorage.removeItem("sms_portal_token");
        toast.success("Giriş başarılı");
        nav("/admin");
      }
    } catch (err) {
      toast.error(fmtErr(err?.response?.data?.detail) || "Giriş başarısız");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen mesh-bg flex items-center justify-center p-6 relative overflow-hidden">
      {/* decorative blobs */}
      <div className="absolute -top-32 -right-32 w-96 h-96 bg-turq-300/30 rounded-full blur-3xl" />
      <div className="absolute -bottom-32 -left-32 w-[28rem] h-[28rem] bg-cyan-300/25 rounded-full blur-3xl" />

      <div className="relative w-full max-w-5xl grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
        {/* Left brand */}
        <div className="hidden lg:block">
          <BrandMark variant="full" className="h-12 w-auto mb-6" />
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/70 backdrop-blur border border-turq-200">
            <Sparkles className="w-3.5 h-3.5 text-turq-600" />
            <span className="tiny-label">E-Telekom · Toplu SMS</span>
          </div>
          <h1 className="font-display font-black text-5xl xl:text-6xl text-slate-900 leading-[0.95] tracking-tight mt-6">
            Mesajlarını <span className="bg-gradient-to-r from-turq-600 to-cyan-500 bg-clip-text text-transparent">kontrol altında</span> tut.
          </h1>
          <p className="mt-6 text-slate-600 max-w-md text-base leading-relaxed">
            Müşteri tabanlı tokenli erişim · dosya yüklemeli toplu gönderim · parametreli şablonlar · zamanlanmış kuyruk yönetimi.
          </p>
          <div className="mt-10 grid grid-cols-3 gap-4 max-w-md">
            {[
              ["200K", "alıcı / iş"],
              ["3", "gönderim modu"],
              ["N×N", "kişiselleştirme"],
            ].map(([n, l]) => (
              <div key={l} className="card-soft p-4">
                <div className="font-display font-black text-2xl bg-gradient-to-br from-turq-600 to-cyan-500 bg-clip-text text-transparent">{n}</div>
                <div className="tiny-label mt-1">{l}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Form card */}
        <form onSubmit={submit} className="glass rounded-2xl p-8 sm:p-10 w-full max-w-md mx-auto shadow-xl" data-testid="admin-login-form">
          <div className="flex items-center gap-3 mb-3">
            <BrandMark variant="full" className="h-9 w-auto lg:hidden" />
            <BrandMark variant="icon" className="w-9 h-9 hidden lg:flex" />
            <div className="tiny-label">Giriş</div>
          </div>
          <h2 className="font-display font-bold text-3xl text-slate-900 mb-7">Panele giriş yap</h2>

          <label className="tiny-label block mb-2">Kullanıcı adı</label>
          <div className="relative mb-5">
            <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-turq-500 pointer-events-none z-10" />
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              data-testid="login-username"
              className="input-soft !pl-11"
              autoFocus
            />
          </div>

          <label className="tiny-label block mb-2">Şifre</label>
          <div className="relative mb-7">
            <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-turq-500 pointer-events-none z-10" />
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="login-password"
              className="input-soft !pl-11"
            />
          </div>

          <button
            disabled={loading}
            type="submit"
            data-testid="login-submit"
            className="w-full btn-primary text-sm font-semibold py-3 rounded-xl disabled:opacity-50"
          >
            {loading ? "Giriş yapılıyor..." : "Giriş Yap →"}
          </button>
        </form>
      </div>
    </div>
  );
}
